//orchestrator_fullstack/frontend/src/app/admin/oidc/api.ts

import { apiFetch } from "../../auth/api";

export type Role = "platform-admin" | "admin" | "operator" | "viewer";
export const TENANT_ADMIN_ROLES: Role[] = ["admin", "operator", "viewer"];
export const ALL_ROLES: Role[] = ["viewer", "operator", "admin", "platform-admin"];

/**
 * Model A UI rule:
 * - tenant-admin should not see/manage platform-admin mappings
 * - platform-admin can see/manage all
 */
export function canManagePlatformAdmin(userRoles: string[] | undefined | null): boolean {
  const rs = (userRoles || []).map((r) => String(r || "").trim().toLowerCase());
  return rs.includes("platform-admin");
}

/** Returns roles allowed for "add mapping" dropdown based on current user roles. */
export function allowedMappingRoles(userRoles: string[] | undefined | null): Role[] {
  return canManagePlatformAdmin(userRoles) ? ALL_ROLES : TENANT_ADMIN_ROLES;
}

/** Filter rows so tenant-admin never sees platform-admin entries (Model A). */
export function filterPlatformAdminRows<T extends { role?: string }>(
  rows: T[] | null | undefined,
  userRoles: string[] | undefined | null
): T[] {
  const list = rows || [];
  if (canManagePlatformAdmin(userRoles)) return list;
  return list.filter((r) => String(r.role || "").trim() !== "platform-admin");
}

export const ROOT_TENANT_UUID = "00000000-0000-0000-0000-000000000001";

/**
 * Normalize a "tenant selector" value to the UUID expected by OIDC mapping endpoints.
 * - Today you use a string tenant key in the tenants table (e.g. "root")
 * - OIDC mapping tables store tenant_id UUID
 *
 * So:
 *   "root" => ROOT_TENANT_UUID
 *   UUID-like string => itself
 */
export function toTenantUuid(tenantKeyOrUuid: string): string {
  const s = String(tenantKeyOrUuid || "").trim();
  if (!s) return "";
  if (s === "root") return ROOT_TENANT_UUID;
  return s; // assume already UUID; backend will validate if not
}

export type EmailRoleRow = { tenant_id: string; email: string; role: string };
export type GroupRoleRow = { tenant_id: string; group_name: string; role: string };
export type GroupTenantRow = { group_name: string; tenant_id: string };

export type ValidateTokenResp = {
  claims_preview: Record<string, any>;
  derived_tenant_id: string;
  derived_roles: string[];
  source: string;
};

export async function listEmailRoles(tenantId: string, hdrs?: Record<string, string>) {
  const tid = toTenantUuid(tenantId);
  return apiFetch<EmailRoleRow[]>(
    `/v1/admin/oidc/email-role?tenant_id=${encodeURIComponent(tid)}`,
    { admin: true, headers: hdrs }
  );
}

export async function addEmailRole(tenantId: string, email: string, role: Role, hdrs?: Record<string, string>) {
  const tid = toTenantUuid(tenantId);
  return apiFetch<{ ok: boolean }>(`/v1/admin/oidc/email-role`, {
    admin: true,
    method: "POST",
    headers: hdrs,
    body: { tenant_id: tid, email, role },
  });
}

export async function removeEmailRole(tenantId: string, email: string, role: Role, hdrs?: Record<string, string>) {
  const tid = toTenantUuid(tenantId);
  const qs = new URLSearchParams({ tenant_id: tid, email, role });
  return apiFetch<{ ok: boolean }>(`/v1/admin/oidc/email-role?${qs.toString()}`, {
    admin: true,
    method: "DELETE",
    headers: hdrs,
  });
}

export async function validateToken(token: string, hdrs?: Record<string, string>) {
  return apiFetch<ValidateTokenResp>(`/v1/admin/oidc/validate-token`, {
    admin: true,
    method: "POST",
    headers: hdrs,
    body: { token },
  });
}

export async function listAudit(tenantId: string, hdrs?: Record<string, string>) {
  const tid = toTenantUuid(tenantId);
  return apiFetch<{ configured: boolean; items: any[] }>(
    `/v1/admin/oidc/audit?tenant_id=${encodeURIComponent(tid)}&limit=100`,
    { admin: true, headers: hdrs }
  );
}

// Existing endpoints already in your backend (from your earlier outputs)
export async function listGroupRoleMappings(tenantId: string, hdrs?: Record<string, string>) {
  const tid = toTenantUuid(tenantId);
  return apiFetch<GroupRoleRow[]>(
    `/v1/admin/oidc/group-role?tenant_id=${encodeURIComponent(tid)}`,
    { admin: true, headers: hdrs }
  );
}

export async function upsertGroupRoleMapping(
  tenantId: string,
  group_name: string,
  role: Role,
  hdrs?: Record<string, string>
) {
  const tid = toTenantUuid(tenantId);
  return apiFetch<any>(`/v1/admin/oidc/group-role`, {
    admin: true,
    method: "POST",
    headers: hdrs,
    body: { tenant_id: tid, group_name, role },
  });
}

export async function deleteGroupRoleMapping(
  tenantId: string,
  group_name: string,
  role: Role,
  hdrs?: Record<string, string>
) {
  const tid = toTenantUuid(tenantId);
  const qs = new URLSearchParams({ tenant_id: tid, group_name, role });
  return apiFetch<any>(`/v1/admin/oidc/group-role?${qs.toString()}`, {
    admin: true,
    method: "DELETE",
    headers: hdrs,
  });
}


export async function listGroupTenantMappings(hdrs?: Record<string, string>) {
  return apiFetch<GroupTenantRow[]>(`/v1/admin/oidc/group-tenant`, { admin: true, headers: hdrs });
}

export async function upsertGroupTenantMapping(
  group_name: string,
  tenant_id: string,
  hdrs?: Record<string, string>
) {
  const tid = toTenantUuid(tenant_id);
  return apiFetch<any>(`/v1/admin/oidc/group-tenant`, {
    admin: true,
    method: "POST",
    headers: hdrs,
    body: { group_name, tenant_id: tid },
  });
}

export async function deleteGroupTenantMapping(group_name: string, hdrs?: Record<string, string>) {
  return apiFetch<any>(`/v1/admin/oidc/group-tenant?group_name=${encodeURIComponent(group_name)}`, {
    admin: true,
    method: "DELETE",
    headers: hdrs,
  });
}

// -----------------------------
// Provisioned / Derived Users (auth_users + auth_user_tenants)
// -----------------------------

export type AuthUserTenantRow = {
  tenant_id: string; // tenant UUID
  sub: string;
  roles: string[];

  email?: string | null;
  name?: string | null;
  picture?: string | null;
  issuer?: string | null;
  audience?: string | null;

  user_created_at?: string | null;
  last_seen_at?: string | null;

  membership_created_at?: string | null;
  membership_updated_at?: string | null;
};

export async function listProvisionedUsers(
  tenantUuid: string,
  q?: string,
  limit: number = 200,
  hdrs?: Record<string, string>
) {
  const qs = new URLSearchParams({
    tenant_id: tenantUuid,
    limit: String(limit),
  });
  if (q && q.trim()) qs.set("q", q.trim());

  return apiFetch<AuthUserTenantRow[]>(
    `/v1/admin/auth/users?${qs.toString()}`,
    { admin: true, headers: hdrs }
  );
}

export async function getProvisionedUserDetail(
  tenantUuid: string,
  sub: string,
  hdrs?: Record<string, string>
) {
  const qs = new URLSearchParams({ tenant_id: tenantUuid });
  return apiFetch<AuthUserTenantRow>(
    `/v1/admin/auth/users/${encodeURIComponent(sub)}?${qs.toString()}`,
    { admin: true, headers: hdrs }
  );
}
